/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  SyntheticEvent,
  useState
} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {
  CTCEntity,
  Entity,
  InventoryDevice,
  IOProfile,
  ManufacturedDevice,
  Operator,
  User
} from '@ion/core/src/types/entities';
import {
  DateFilterMegaMenu
} from '@ion/dls/src/components/menus/DateFilterMegaMenu';
import {useExtendStyles} from '../../hooks/useExtendStyles';
import {toggleThemeStyle} from '../../theme/helpers';
import {DLSTheme} from '../../types/types';
import EditorDialog from '../EditorDialog';
import styles from '../editorStyles';
import AuditGrid, {AuditGridOptions} from './AuditGrid';
import {useDateFilter} from './useDateFilter';

export interface AuditDateFilterProps {
  enableDateFilter?: boolean;
  enableRangeSelection?: boolean;
};

export interface AuditDialogProps extends AuditGridOptions, AuditDateFilterProps {
  item: CTCEntity;
  entity: Entity;
  onCloseDialog?: (event: SyntheticEvent<{}, Event>) => void;
  onViewAuditChanges?: Function;
};

const overrides = (theme: DLSTheme) => ({
  form : {
    height : '100%'
  },
  formPaper : {
    minWidth    : '70% !important',
    paddingLeft : '2px !important',
    maxHeight   : '574px',
    height      : '574px'
  },
  paperScrollPaper : {
    minHeight                      : 'calc(75% - 94px)',
    [theme.breakpoints.down('sm')] : {
      minHeight : 'calc(75% - 94px)'
    }
  },
  filter : {
    textAlign    : 'end',
    marginBottom : '10px'
  },
  // EditorDialog's formTitle already centers its content via justifyContent,
  // but dialogTitle's own marginLeft offsets it off-center; dropping the
  // margin here (current theme only) lets it center correctly ...
  dialogTitle : {
    marginLeft : toggleThemeStyle('20px', 0)
  }
});

/**
 * @AuditDialog
 *
 * The AuditDialog provides the UI for reviewing the change history of a selected entity.
 *
 * ```typescript
 *
 *     import AuditDialog from '@ion/dls/src/editors/audit/AuditDialog';
 *
 *     <AuditDialog item={auditAsset} entity={Entity.asset} onCloseDialog={onAuditClose} />
 *
 *     // customizes how the audit log is requested and displayed ...
 *     <AuditDialog item={vehicle} entity={Entity.vescout} transformOptions={SCOUT_AUDIT_OPTIONS} />
 *
 * ```
 */
export const AuditDialog = ({
  enableDateFilter,
  item,
  entity,
  onCloseDialog,
  onViewAuditChanges,
  rowMenusEnabled = false,
  enableRangeSelection = false,
  showAuditVersion = false,
  showDifferences = false,
  transformOptions
}: AuditDialogProps) => {
  const classes                             = useExtendStyles(styles, overrides)();
  const [hasFileHistory, setHasFileHistory] = useState<boolean>(false);
  const {
    selectedFilterValue,
    setSelectedFilterValue,
    selectedRange,
    setSelectedDateValue,
    handleSubmit
  } = useDateFilter();

  function getTitleName(item: CTCEntity) {
    switch (entity) {
      case Entity.ioprofile: {
        const {profileName = ''} = item as IOProfile;
        return profileName;
      }
      case Entity.operator: {
        const {firstName = '', lastName = ''} = item as Operator;
        return `${firstName} ${lastName}`;
      }
      case Entity.user: {
        const {username = ''} = item as User;
        return username;
      }
      case Entity.deviceprovisioning: {
        const {serialNumber = ''} = (item as InventoryDevice) || {};
        return serialNumber;
      }
      case Entity.manufactureddevice: {
        const {primaryDeviceIdentifier = ''} = (item as ManufacturedDevice) || {};
        return primaryDeviceIdentifier;
      }
      case Entity.vescout: {
        const {vin = '', esn = ''} = (item as {vin?: string; esn?: string}) || {};
        return vin || esn;
      }
    }
    return item?.name;
  };

  function getTitleKey() {
    if (entity === Entity.vescout) {
      return 'vescoutTitle';
    }
    return hasFileHistory ? 'titleWithFile' : 'title';
  };

  return (
    <EditorDialog open
      dialogTitle={translate(`common:editors.audit.${getTitleKey()}`, {name: getTitleName(item)})}
      onCloseDialog={onCloseDialog}
      onCancelDialog={onCloseDialog}
      styleOverrides={overrides}
      omitActions>
      {enableDateFilter && <div className={classes.filter}>
        <DateFilterMegaMenu onSubmit={handleSubmit}
          setSelectedDateValue={setSelectedDateValue}
          selectedFilterValue={selectedFilterValue}
          setSelectedFilterValue={setSelectedFilterValue}
          dateRangeFromToday={true}
          menuLeft={'0px'}
          minMenuWidth={'100%'} />
      </div>}
      <form className={classes.form} style={{overflow: 'unset', height: '100%'}}>
        <AuditGrid item={item}
          showDifferences={showDifferences}
          rowMenusEnabled={rowMenusEnabled}
          showAuditVersion={showAuditVersion}
          selectedRange={selectedRange}
          enableDateFilter={enableDateFilter}
          enableRangeSelection={enableRangeSelection}
          setHasFileHistory={setHasFileHistory}
          entity={entity}
          transformOptions={transformOptions}
          onViewAuditChanges={onViewAuditChanges} />
      </form>
    </EditorDialog>
  );
};

export default AuditDialog;
